'use client';

import {useCallback, useEffect, useRef, useState} from 'react';

// True while the element scrolls AND is not scrolled to the end, so a bottom
// fade can signal "there is more below" without dimming the final rows of a
// list that already fits.
//
// The node is taken through a callback ref, not an object ref: the target lives
// inside a portal that mounts after this hook's effects would first run, so an
// object ref is still null at that point and nothing would ever measure.
export function useScrollFade(enabled: boolean) {
  const [element, setElement] = useState<HTMLElement | null>(null);
  const [faded, setFaded] = useState(false);
  const observerRef = useRef<ResizeObserver | null>(null);

  const ref = useCallback((node: HTMLElement | null) => {
    setElement(node);
  }, []);

  useEffect(() => {
    if (!element || !enabled) {
      setFaded(false);
      return;
    }
    const measure = () => {
      setFaded(element.scrollHeight - element.clientHeight - element.scrollTop > 1);
    };
    measure();
    element.addEventListener('scroll', measure, {passive: true});
    // The scroller's own box does not change when its content grows, so the
    // content has to be observed too or the first measurement is the only one.
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    for (const child of element.children) {
      observer.observe(child);
    }
    observerRef.current = observer;
    return () => {
      element.removeEventListener('scroll', measure);
      observer.disconnect();
      observerRef.current = null;
    };
  }, [element, enabled]);

  return {ref, faded};
}
